
import React from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { white } from '@zocdoc/branding/colors';
import { breakpoint } from '@zocdoc/components/src/grid';
import Lazyload from '@zocdoc/components/src/LazyLoad';
import FallbackImage from './FallbackImage.svg';
import {
    PHOTO_SIZE_LARGE,
    PHOTO_SIZE_MEDIUM,
    PHOTO_SIZE_XXSMALL,
} from '../constants';

export class DoctorPhoto extends React.PureComponent {
    static propTypes = {
        isProduction: PropTypes.bool,
        provider: PropTypes.shape({
            circlePictureUrl: PropTypes.string,
            nameInSentence: PropTypes.string,
            squarePictureUrl: PropTypes.string,
        }),
    };

    render() {
        return (
            <Wrapper>
                {this.renderPhoto()}
                {this.props.children}
            </Wrapper>
        );
    }

    renderPhoto() {
        const {
            isProduction,
            provider,
        } = this.props;

        if (provider != null && (provider.circlePictureUrl || provider.squarePictureUrl)) {
            if (!provider.circlePictureUrl || (provider.circlePictureUrl === provider.squarePictureUrl)) {
                return (
                    <CircleMask data-test="doctor-card-photo-mask">
                        {this.renderPicture(provider.nameInSentence, provider.squarePictureUrl, isProduction)}
                    </CircleMask>
                );
            }

            return this.renderPicture(provider.nameInSentence, provider.circlePictureUrl, isProduction);
        }

        return this.renderFallbackImage(provider);
    }

    renderFallbackImage(provider) {
        const altText = provider ? provider.nameInSentence : 'doctor photo';
        return this.renderImage(altText, FallbackImage);
    }

    renderImage(altText, imageSource) {
        return (
            <Img
                alt={altText}
                data-test="doctor-card-photo-image"
                itemProp="image"
                src={imageSource}
            />
        );
    }

    renderPicture(altText, imageSource, isProduction) {
        return (
            <StyledLazyload>
                <picture>
                    {!isProduction ? <source
                        srcSet={`${imageSource}?&fm=webp`}
                        type="image/webp"
                    /> : null}
                    {this.renderImage(altText, imageSource)}
                </picture>
            </StyledLazyload>
        );
    }
}

// LazyLoad needs height/width defined to know the size when the photo is off the screen
const StyledLazyload = styled(Lazyload)`
    align-items: center;
    display: flex;

    width: ${PHOTO_SIZE_XXSMALL}px;
    height: ${PHOTO_SIZE_XXSMALL}px;

    ${breakpoint.medium} {
        width: ${PHOTO_SIZE_MEDIUM}px;
        height: ${PHOTO_SIZE_MEDIUM}px;
    }

    ${breakpoint.large} {
        width: ${PHOTO_SIZE_LARGE}px;
        height: ${PHOTO_SIZE_LARGE}px;
    }
`;

const CircleMask = styled.div`
    border-color: ${white};
    border-radius: 50%;
    overflow: hidden;
`;

export const PhotoContainer = styled.div`
    margin-right: 22px;

    ${breakpoint.small} {
        margin-right: 24px;
    }

    ${breakpoint.medium} {
        margin-right: 28px;
    }

    ${breakpoint.large} {
        margin-right: 40px;
    }
`;

const fadeIn = keyframes`
    0% {
        opacity: 0;
    }

    100% {
        opacity: 1;
    }
`;

const Img = styled.img`
    animation: 0.3s ${fadeIn} ease-out;
    width: ${PHOTO_SIZE_XXSMALL}px;

    ${breakpoint.medium} {
        width: ${PHOTO_SIZE_MEDIUM}px;
    }

    ${breakpoint.large} {
        width: ${PHOTO_SIZE_LARGE}px;
    }
`;

const Wrapper = styled.div`
    position: relative;
`;
